import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { PAGE_SEO } from '../seo';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead page={PAGE_SEO.privacy} />
      <Header />
      <div className="flex-1 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-pb-gray-500 hover:text-pb-orange mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-display font-bold text-pb-gray-800 mb-2">
            Privacy Policy
          </h1>
          <p className="text-pb-gray-500">Last updated: February 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-rhythmic-xl p-8 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              1. Introduction
            </h2>
            <p className="text-pb-gray-600 leading-relaxed">
              PartnerBuddy ("we", "our", or "us") is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your
              information when you use our Slack and Microsoft Teams applications and associated services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              2. Information We Collect
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>We collect the following types of information:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Slack Workspace Information:</strong> Workspace ID, workspace name,
                  and authorized channel IDs for bot operation
                </li>
                <li>
                  <strong>User Information:</strong> Slack user IDs of users who interact
                  with the bot (we do not collect email addresses without consent)
                </li>
                <li>
                  <strong>Message Content:</strong> Messages directed to the bot for processing
                  (messages are processed in real-time and not stored long-term)
                </li>
                <li>
                  <strong>AWS Account Information:</strong> IAM role ARN and external ID for
                  accessing your AWS Partner Central data
                </li>
                <li>
                  <strong>CRM Credentials:</strong> Encrypted API tokens for HubSpot or
                  Salesforce integration (if configured)
                </li>
                <li>
                  <strong>Organization Details:</strong> Company name, admin email, and timezone
                  preferences
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              3. How We Use Your Information
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>We use the collected information to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Process natural language queries about AWS ACE opportunities</li>
                <li>Sync data between AWS Partner Central and your CRM system</li>
                <li>Generate reports and insights on your partner opportunities</li>
                <li>Authenticate and authorize access to your workspace</li>
                <li>Improve and maintain our service</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              4. AI Processing Disclosure
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>
                PartnerBuddy uses artificial intelligence (AI) powered by Amazon Bedrock
                to process and respond to your queries. Please be aware:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Data Retention:</strong> Messages sent to the AI are processed
                  in real-time and not stored by the AI provider
                </li>
                <li>
                  <strong>No Training:</strong> Your data is NOT used to train AI models
                </li>
                <li>
                  <strong>Accuracy:</strong> AI-generated responses may contain inaccuracies;
                  always verify critical information
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              5. Data Retention
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>We retain data according to the following schedule:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Session Data:</strong> Conversation context is retained for
                  24 hours to maintain conversation continuity, then automatically deleted
                </li>
                <li>
                  <strong>Configuration Data:</strong> Workspace settings and credentials
                  are retained while your subscription is active
                </li>
                <li>
                  <strong>Cached Data:</strong> API responses are cached for up to 5 minutes
                  to improve performance
                </li>
                <li>
                  <strong>Audit Logs:</strong> Access logs are retained for 90 days for
                  security purposes
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              6. Data Security
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>We implement robust security measures to protect your data:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>All data is encrypted in transit using TLS 1.2 or higher</li>
                <li>Sensitive credentials are encrypted at rest using AWS KMS</li>
                <li>API tokens are stored in AWS Secrets Manager</li>
                <li>Access is controlled through IAM roles with least-privilege principles</li>
                <li>Regular security audits and monitoring</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              7. Data Sharing
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>We do not sell your data. We may share data only in these cases:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Service Providers:</strong> AWS services (DynamoDB, Lambda, Bedrock)
                  to operate our service
                </li>
                <li>
                  <strong>Your Integrations:</strong> With AWS Partner Central, HubSpot, or
                  Salesforce as configured by you
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law or to protect
                  our legal rights
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              8. Your Rights
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>You have the following rights regarding your data:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Access:</strong> Request a copy of the data we hold about your
                  workspace
                </li>
                <li>
                  <strong>Correction:</strong> Request correction of inaccurate data
                </li>
                <li>
                  <strong>Deletion:</strong> Request deletion of your data (see section 9)
                </li>
                <li>
                  <strong>Portability:</strong> Request your data in a machine-readable format
                </li>
                <li>
                  <strong>Objection:</strong> Object to certain processing activities
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              9. Data Deletion Requests
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>
                To request deletion of your data, you can either:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  Contact us at{' '}
                  <a href="mailto:support@rhythmic.dev" className="text-pb-orange hover:underline">
                    support@rhythmic.dev
                  </a>
                </li>
                <li>
                  Use the "Delete Account" option in your setup portal
                </li>
              </ul>
              <p className="mt-4">
                Upon receiving a deletion request, we will delete your data within 30 days
                and confirm the deletion via email.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              10. Contact Information
            </h2>
            <div className="space-y-4 text-pb-gray-600 leading-relaxed">
              <p>
                For questions about this Privacy Policy or to exercise your rights,
                contact us at:
              </p>
              <div className="bg-pb-gray-50 rounded-lg p-4 mt-4">
                <p><strong>Email:</strong>{' '}
                  <a href="mailto:support@rhythmic.dev" className="text-pb-orange hover:underline">
                    support@rhythmic.dev
                  </a>
                </p>
                <p className="mt-2"><strong>Response Time:</strong> Within 2 business days</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-pb-gray-800 mb-4">
              11. Changes to This Policy
            </h2>
            <p className="text-pb-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of
              any changes by posting the new Privacy Policy on this page and updating the
              "Last updated" date.
            </p>
          </section>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
